import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";

const FAQS = [
  {
    q: "Is the houseboat warm in winter?",
    a: "Yes — the houseboat is fully heated with a modern electric heating system throughout. The cabin is well insulated and cosy even on cold Irish nights. Blankets and throws are provided for extra comfort on the decks.",
  },
  {
    q: "Does the houseboat move?",
    a: "The houseboat stays securely moored at Inner Lakes Marina. You'll feel a gentle sway with the breeze or passing boats, but it won't move from its berth. Many guests find this gentle motion the most relaxing part of the stay.",
  },
  {
    q: "Is it suitable for children?",
    a: "The houseboat is best suited to adults and older children. The surrounding marina is a working environment and water safety is paramount — children must be supervised at all times near the water. Life jackets are available on board.",
  },
  {
    q: "Are pets allowed?",
    a: "Please get in touch before booking with a pet. We consider each request individually — well-behaved dogs are often welcome.",
  },
  {
    q: "Is there Wi-Fi?",
    a: "Yes, free high-speed Wi-Fi is included. The connection is reliable for streaming, remote work and video calls.",
  },
  {
    q: "What are the check-in and check-out times?",
    a: "Check-in is from 3:00 pm and check-out is by 11:00 am. Early check-in or late check-out may be possible on request — just ask when you book.",
  },
  {
    q: "How does self check-in work?",
    a: "Full arrival instructions and a lockbox code are sent to you before your stay. You don't need to meet anyone — just follow the directions, grab the key from the lockbox and you're aboard.",
  },
  {
    q: "Is there parking at the marina?",
    a: "Yes, free private parking is available directly alongside the houseboat at Inner Lakes Marina.",
  },
  {
    q: "What is the cancellation policy?",
    a: "Cancellation terms are shown at the time of booking on our booking platform. We recommend reviewing these before confirming. For direct enquiries, contact us and we'll explain the options.",
  },
  {
    q: "How far is Athlone town centre?",
    a: "Inner Lakes Marina is approximately 8 km from Athlone town centre — about 10 minutes by car. The Chalets Restaurant is 1 km away and Glasson Lakehouse Hotel is 1.5 km, both within easy walking or cycling distance.",
  },
  {
    q: "Is there a minimum stay?",
    a: "The minimum stay is 1 night, though 2 nights is recommended to make the most of the location. Longer stays are available and often better value.",
  },
  {
    q: "What is included in the price?",
    a: "All linen, towels, heating, Wi-Fi, parking and welcome essentials (tea, coffee, milk) are included. The full kitchen means you can self-cater for the duration of your stay.",
  },
];

export default function Faq() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="bg-[#FAFAF8] text-[#1C1C1A] min-h-screen">
      <Helmet>
        <title>Houseboat Stay FAQs | Aquastays</title>
        <meta name="description" content="Is it warm in winter? Does it move? Kids, pets, Wi-Fi, check-in times and cancellation — every question answered about staying on our Lough Ree houseboat." />
        <link rel="canonical" href="https://aquastays.com/faq" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Header solid />

      <main className="max-w-[860px] mx-auto px-6 sm:px-10 py-20 sm:py-28">
        <p className="eyebrow text-[#6B6B65] mb-4">Frequently asked questions</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-light text-[#1C1C1A] tracking-tight leading-tight mb-6">
          Every question answered.
        </h1>
        <p className="text-[#6B6B65] text-sm font-light leading-relaxed mb-14 max-w-xl">
          Everything you need to know before you arrive. Can't find what you're looking for?{" "}
          <a href="/#contact" className="underline underline-offset-2 hover:text-[#1C1C1A] transition-colors">
            Drop us a message
          </a>{" "}
          and we'll reply within a few hours.
        </p>

        <div className="divide-y divide-[#1C1C1A]/8">
          {FAQS.map((f) => (
            <div key={f.q} className="py-7">
              <h2 className="font-medium text-[#1C1C1A] text-base mb-2">{f.q}</h2>
              <p className="text-sm text-[#6B6B65] leading-relaxed font-light">{f.a}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 pt-10 border-t border-[#1C1C1A]/8 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <p className="text-sm text-[#6B6B65] font-light">Ready to book your stay?</p>
          <div className="flex gap-4 flex-wrap">
            <Link to="/boats/aquastays-drift" className="text-[0.68rem] font-medium uppercase tracking-[0.18em] text-[#1C1C1A] hover:text-[#E8970A] transition-colors">
              View the houseboat →
            </Link>
            <a href="https://aquastays.guestybookings.com/en/properties/69dff123051f000012a8ddb6" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#E8970A] hover:bg-[#D08500] text-white font-semibold text-sm transition-colors">
              Check availability
            </a>
          </div>
        </div>

        <p className="mt-10 text-xs text-[#6B6B65]">
          Also see our <Link to="/getting-here" className="underline underline-offset-2 hover:text-[#1C1C1A]">getting here guide</Link> and <Link to="/lough-ree-things-to-do" className="underline underline-offset-2 hover:text-[#1C1C1A]">things to do on Lough Ree</Link>.
        </p>
      </main>

      <Footer />
    </div>
  );
}
